import {
  Component,
  ContentChild,
  Directive,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import { ExtractExpr } from '../../utils/item-expr.utils';

export interface TreeItemLike<T extends TreeItemLike = TreeItemLike<any>> {
  icon?: string;
  text?: string;
  link?: string;
  linkTarget?: string;
  tooltip?: string;
  selected?: boolean;
  disabled?: boolean;
  visible?: boolean;
  opened?: boolean;
  hasItems?: boolean;
  openedIndicatorEnabled?: boolean;
  canToggleOpened?: boolean;
  items?: T[];
  onClick?: (e) => void;
  itemTemplate?: TemplateRef<TreeListItemContext<T>>;
  subListTemplate?: TemplateRef<TreeListItemContext<T>>;
}

interface TreeListItemContext<T extends TreeItemLike = TreeItemLike> {
  index: number;
  item: T;
}

@Directive({
  selector: 'ng-template[treeItem]',
})
export class TreeListItemTemplate<T extends TreeItemLike = TreeItemLike> {
  constructor(public templateRef: TemplateRef<TreeListItemContext<T>>) {}
}

@Component({
  selector: 'app-tree-list',
  templateUrl: 'tree-list.component.html',
  styleUrls: ['tree-list.component.scss'],
})
export class TreeListComponent<T extends TreeItemLike = TreeItemLike> {
  @ContentChild(TreeListItemTemplate, { static: false })
  treeItemTpl: TreeListItemTemplate<T>;

  @Input()
  items: TreeItemLike[] = [];
  @Input()
  hintEnabled = false;
  @Input()
  tooltipEnabled = false;
  @Input()
  textEnabled = true;
  @Input()
  openedIndicatorEnabled = true;
  @Input()
  openedIndicatorAtLeft = false;

  @Input()
  openedSingleMode = true;

  @Input()
  textEllipsisMode = false;

  @Input()
  deferRendering = true;

  @Input()
  scrollable = true;

  @Input()
  keyExpr: ExtractExpr;

  @Output()
  onItemClick = new EventEmitter<any>();
  @Output()
  onItemOpenChanged = new EventEmitter<TreeItemLike>();

  closeItems() {
    this.items.forEach((item) => {
      if (item.opened) {
        item.opened = false;
      }
    });
  }

  onItemClickInternal(e, item: TreeItemLike, parentItem: TreeItemLike) {
    e.itemData = item;
    e.parentItemData = parentItem;
    item.onClick?.(e);

    if (!e.defaultPrevented && item.link) {
      window.open(item.link, item.linkTarget ?? '_self');
    }

    if (!e.defaultPrevented) {
      this.onItemClick.emit(e);
    }

    if (
      !e.defaultPrevented &&
      this.shouldShowItems(item) &&
      item.canToggleOpened !== false
    ) {
      if (this.openedSingleMode && !item.opened) {
        const items = parentItem?.items ?? this.items;

        for (let aux of items) {
          if (aux.opened) {
            aux.opened = false;
            this.onItemOpenChanged.emit(aux);
          }
        }
      }

      item.opened = !item.opened;
      this.onItemOpenChanged.emit(item);
    }
  }

  shouldShowIndicator(item: TreeItemLike) {
    if (typeof item.openedIndicatorEnabled === 'boolean') {
      return item.openedIndicatorEnabled;
    }

    return this.shouldShowItems(item);
  }

  shouldShowItems(item: TreeItemLike) {
    if (typeof item.hasItems === 'boolean') {
      return item.hasItems;
    }

    return !!item.items?.length;
  }
}
